// What a RECEIVED message links to, in-app: a `~channel` (ChannelLinks.spans over the open team's
// catalogue — only KNOWN slugs, like the Mac) opens that channel, and a permalink card (what a
// forwarded message renders as, LinkPreviewCard) jumps to the quoted message instead of leaving
// the app.
import { useContext, type ReactNode } from 'react';
import { InlineMarkdown } from '../../../core/inlineMarkdown';
import type { PostEmbed } from '../../../core/postEmbed';
import { Icon } from '../../../design/icons';
import { ShellContext } from '../../shell/ShellContext';
import { useChannelCatalogue } from './composeSignals';
import './compose.css';

/** The slugs MessageBody hands to MessageAttributedText (empty outside a workspace). */
export function useKnownChannelSlugs(): ReadonlySet<string> {
  return useChannelCatalogue().slugs;
}

export function ChannelLink({ slug, children }: { slug: string; children: ReactNode }) {
  const shell = useContext(ShellContext);
  const { idBySlug } = useChannelCatalogue();
  const open = () => {
    const id = idBySlug.get(slug);
    if (id && shell) shell.openChannel(id);
  };
  return (
    <span
      className="compose-channel-link"
      role="link"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        open();
      }}
      onKeyDown={(e) => e.key === 'Enter' && open()}
    >
      {children}
    </span>
  );
}

export function PermalinkCard({ embed }: { embed: PostEmbed }) {
  const shell = useContext(ShellContext);
  const snippet = InlineMarkdown.parse(embed.description).text;
  return (
    <button type="button" className="compose-permalink" onClick={() => shell?.jumpToPost(embed.targetPostId)}>
      <span className="rule" />
      <span className="body">
        {embed.channelDisplayName ? (
          <span className="where">
            <Icon name="hash" size={11} />
            {embed.channelDisplayName}
          </span>
        ) : null}
        <span className="snippet">{snippet}</span>
      </span>
    </button>
  );
}
